import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { JointFactory } from "./joints/jointFactory";
import { JOINT_MATERIALS, JOINT_SPHERE_SIZES, JOINT_SPHERE_SEGMENTS } from "./joints/jointStyles";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { hasPhysicsWorker } from "../physics/workerHandle";
import { applyJointLabelDragViaActuatorPositionControls } from "../physics/jointLabelActuatorDrag";
import { logger } from "../utils/logger";
import {
  applyJointLabelLayoutStyles,
  applyLabelStyles,
  JOINT_LABEL_ACTUATOR_ICON_CLASS,
  JOINT_LABEL_CLASS,
  JOINT_LABEL_TEXT_CLASS,
} from "./labelUtils";
import {
  applyMarkerLabelCss2dLayout,
  JOINT_MARKER_LABEL_LIFT_LOCAL_METERS,
} from "./coincidentLabelSpread";

const sphereGeometryCache = new Map();

function getSphereGeometry(radius, widthSegments, heightSegments) {
  const safeRadius = Number.isFinite(Number(radius)) ? Number(radius) : 1;
  const safeWidthSegments = Math.max(3, Math.floor(Number(widthSegments) || 8));
  const safeHeightSegments = Math.max(2, Math.floor(Number(heightSegments) || 6));
  const key = `${safeRadius}:${safeWidthSegments}:${safeHeightSegments}`;
  let geometry = sphereGeometryCache.get(key);
  if (!geometry) {
    geometry = new THREE.SphereGeometry(safeRadius, safeWidthSegments, safeHeightSegments);
    sphereGeometryCache.set(key, geometry);
  }
  return geometry;
}

function getJointCenterPickRadius(jointSpecificViz, fallbackRadius, jointType) {
  if (jointType === "slide") {
    return fallbackRadius;
  }

  const explicitArcRadius = Number(jointSpecificViz?.userData?.arcRadius);
  if (Number.isFinite(explicitArcRadius) && explicitArcRadius > 0) {
    return explicitArcRadius;
  }

  let maxLineRadius = 0;
  const point = new THREE.Vector3();
  jointSpecificViz?.traverse((child) => {
    const position = child?.geometry?.attributes?.position;
    if (!child?.isLine || !position) return;

    child.updateMatrix();
    for (let i = 0; i < position.count; i += 1) {
      point.fromBufferAttribute(position, i).applyMatrix4(child.matrix);
      maxLineRadius = Math.max(maxLineRadius, point.length());
    }
  });

  return maxLineRadius > 0 ? maxLineRadius : fallbackRadius;
}

function getJointSelectionHighlightRadius(jointType, centerRadius, pickRadius) {
  if ((jointType === "hinge" || jointType === "ball") && pickRadius > centerRadius) {
    return centerRadius + (pickRadius - centerRadius) * 0.6;
  }
  return pickRadius;
}

export function createJointVisualization(
  jointInfo,
  parentRot,
  bodyQuat,
  groupPosition,
  showJoints,
  jointMarkerSizes,
  sceneActions, // Add sceneActions as parameter
  jointVisualizationOptions = null
) {
  const { name: jointName, kind: jointKind, type: jointType } = jointInfo;
  // Use kind if available, fallback to type for compatibility
  const jointTypeResolved = jointKind || jointType;
  if (jointTypeResolved === "free") return null;

  // Fallback to global store actions if none provided
  const actions =
    sceneActions || (typeof getSceneActions === "function" ? getSceneActions() : null);

  // Create a group for all joint visualization elements
  const jointVizGroup = new THREE.Group();
  jointVizGroup.userData.isJointVizGroup = true;
  jointVizGroup.userData.jointName = jointName;
  jointVizGroup.userData.jointUuid = jointInfo.uuid; // Store UUID for selection
  jointVizGroup.userData.jointType = jointTypeResolved;

  // Place the joint viz at the joint's local position within the body group
  const jointPos = Array.isArray(jointInfo?.position) ? jointInfo.position : [0, 0, 0];
  jointVizGroup.position.set(...jointPos);

  // Only add the center marker for non-slide joints
  if (jointTypeResolved !== "slide") {
    const markerGroup = new THREE.Group();

    // Main marker sphere
    const marker = new THREE.Mesh(
      getSphereGeometry(
        jointMarkerSizes ? jointMarkerSizes.center : JOINT_SPHERE_SIZES.center,
        JOINT_SPHERE_SEGMENTS.center,
        JOINT_SPHERE_SEGMENTS.center
      ),
      new THREE.MeshBasicMaterial(JOINT_MATERIALS.center)
    );
    marker.position.set(0, 0, 0);
    marker.visible = showJoints;
    // Make the marker always strongly visible regardless of camera angle/occlusion
    marker.renderOrder = 10000;
    if (marker.material) {
      marker.material.transparent = true;
      marker.material.opacity = 1;
      marker.material.depthTest = false;
      marker.material.depthWrite = false;
      marker.material.needsUpdate = true;
    }
    marker.name = jointName || "";
    marker.userData.jointName = jointName || "";
    marker.userData.jointUuid = jointInfo.uuid; // Store UUID for selection
    markerGroup.add(marker);
    // Keep an explicit center marker reference so selection visibility logic never
    // has to infer from traversal order (which can accidentally pick glow meshes).
    jointVizGroup.userData.centerSphere = marker;

    jointVizGroup.add(markerGroup);
  }

  // Create joint-specific visualization using the factory
  // Pass parentRot for world-space range indicators
  const jointSpecificViz = JointFactory.createJointVisualization(
    jointInfo,
    parentRot, // Use parentRot for world-space range indicators
    showJoints,
    jointMarkerSizes,
    jointVisualizationOptions
  );

  if (jointSpecificViz) {
    jointVizGroup.add(jointSpecificViz);
    // Copy userData from specific visualization to main group
    Object.assign(jointVizGroup.userData, jointSpecificViz.userData);
  }

  const centerMarkerRadius = jointMarkerSizes ? jointMarkerSizes.center : JOINT_SPHERE_SIZES.center;
  const centerPickRadius = getJointCenterPickRadius(
    jointSpecificViz,
    centerMarkerRadius,
    jointTypeResolved
  );
  jointVizGroup.userData.selectionHighlightRadius = getJointSelectionHighlightRadius(
    jointTypeResolved,
    centerMarkerRadius,
    centerPickRadius
  );
  const centerPickProxy = new THREE.Mesh(
    getSphereGeometry(centerPickRadius, JOINT_SPHERE_SEGMENTS.center, JOINT_SPHERE_SEGMENTS.center),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    })
  );
  centerPickProxy.name = `${jointName || jointInfo.uuid || "joint"}_centerPickProxy`;
  centerPickProxy.visible = showJoints;
  centerPickProxy.renderOrder = 10002;
  centerPickProxy.userData.isJointCenterPickProxy = true;
  centerPickProxy.userData.jointUuid = jointInfo.uuid;
  centerPickProxy.userData.jointName = jointName || "";
  jointVizGroup.add(centerPickProxy);
  jointVizGroup.userData.centerPickProxy = centerPickProxy;

  // Joint label
  if (jointName) {
    // Add click handler for joint selection
    const handleLabelClick = (e) => {
      e.stopPropagation();
      const event = new CustomEvent("joint-label-click", {
        detail: {
          jointUuid: jointInfo.uuid,
          jointName,
          clientX: e.clientX,
          clientY: e.clientY,
        }, // Include UUID for selection
      });
      window.dispatchEvent(event);
    };
    const labelDiv = document.createElement("div");
    labelDiv.className = JOINT_LABEL_CLASS;
    applyLabelStyles(labelDiv);
    applyJointLabelLayoutStyles(labelDiv);

    const iconWrap = document.createElement("span");
    iconWrap.className = JOINT_LABEL_ACTUATOR_ICON_CLASS;
    iconWrap.setAttribute("aria-hidden", "true");
    iconWrap.style.setProperty("display", "none", "important");
    iconWrap.style.setProperty("align-items", "center", "important");
    iconWrap.style.setProperty("justify-content", "center", "important");
    iconWrap.style.setProperty("flex-shrink", "0", "important");
    iconWrap.style.setProperty("margin-inline-end", "4px", "important");
    iconWrap.style.lineHeight = "0";
    iconWrap.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.4-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.4 10.2a.5.5 0 0 1-.86-.46l-1.92-6.02A1 1 0 0 0 11 14z"/></svg>';

    const textSpan = document.createElement("span");
    textSpan.className = JOINT_LABEL_TEXT_CLASS;
    textSpan.textContent = jointName;

    labelDiv.appendChild(iconWrap);
    labelDiv.appendChild(textSpan);

    labelDiv.addEventListener("click", handleLabelClick);
    labelDiv.cleanupManagedLabel = () => {
      labelDiv.removeEventListener("click", handleLabelClick);
    };

    // --- Drag-to-set-value functionality ---
    let dragging = false;
    let startX = 0;
    let startValue = 0;

    // Create event handlers
    const handleMouseDown = (e) => {
      if (e.button !== 0) return; // Only left mouse button
      if (dragging) return;
      const jointUuid = jointInfo.uuid;
      if (!jointUuid) {
        logger.warn("[jointVisualization] Joint UUID not available for", jointName);
        return;
      }
      dragging = true;
      startX = e.clientX;
      const state = getSceneState();
      const joint = state.joints[jointUuid];
      const isScalarJoint = joint && (joint.kind === "hinge" || joint.kind === "slide");
      startValue = isScalarJoint && joint ? joint.ref : 0;
      document.body.style.cursor = "ew-resize";
      e.preventDefault();
      e.stopPropagation();
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
      if (!dragging) return;
      if (!actions) return;
      const jointUuid = jointInfo.uuid;
      if (!jointUuid) {
        logger.warn("[jointVisualization] Joint UUID not available for", jointName);
        return;
      }
      const dx = e.clientX - startX;
      // Get current joint range directly from state
      const state = getSceneState();
      const joint = state.joints[jointUuid];
      const isScalarJoint = joint && (joint.kind === "hinge" || joint.kind === "slide");
      const [jointMin, jointMax] = isScalarJoint && joint.range ? joint.range : [-Math.PI, Math.PI];

      // Sensitivity: 150px for full range
      const sensitivity = 150;
      const delta = ((jointMax - jointMin) * dx) / sensitivity;
      const newValue = Math.max(jointMin, Math.min(jointMax, startValue + delta));

      const physics = usePhysicsStore.getState();
      const physicsOn = physics.isRunning === true && hasPhysicsWorker();
      if (physicsOn) {
        const viaActuators = applyJointLabelDragViaActuatorPositionControls(jointUuid, newValue);
        if (!viaActuators) {
          actions.updateJointValue(jointUuid, newValue);
        }
      } else {
        actions.updateJointValue(jointUuid, newValue);
      }
    };

    const handleMouseUp = () => {
      if (dragging) {
        dragging = false;
        document.body.style.cursor = "";
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    // Add event listeners
    labelDiv.addEventListener("mousedown", handleMouseDown);

    // Store cleanup function for later removal
    labelDiv.cleanupDragListeners = () => {
      if (typeof labelDiv.cleanupManagedLabel === "function") {
        labelDiv.cleanupManagedLabel();
      }
      labelDiv.removeEventListener("mousedown", handleMouseDown);
      dragging = false;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // --- End drag-to-set-value ---

    const label = new CSS2DObject(labelDiv);
    applyMarkerLabelCss2dLayout(label);
    label.position.set(0, JOINT_MARKER_LABEL_LIFT_LOCAL_METERS, 0);
    label.userData.stackSpreadSortKey = `joint:${jointInfo.uuid}`;
    label.userData.stackSpreadBaseLocal = new THREE.Vector3(
      0,
      JOINT_MARKER_LABEL_LIFT_LOCAL_METERS,
      0
    );
    label.visible = showJoints;
    jointVizGroup.add(label);
    jointVizGroup.userData.siteLabel = label;
  }

  return jointVizGroup;
}
